import { useState } from 'react'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

import { useCalendarData, CalendarData, ExtendedProps } from './useCalendarData'
import FullCalendar from './FullCalendar'
import {
  roomSVG,
  placeSVG,
  personSVG,
  selectContributorSVG,
  outsideRoomSVG,
  lungsSrc,
  heartSrc,
  vasculaireSrc,
  congenitalSrc,
  onDateSet,
} from './FullCalendar.utils'
import { formatHumanTime } from '../../../../utils'

interface ResourceGridCalendarProps {
  data: CalendarData
  disableEventClick?: boolean
  showWithoutSession?: boolean
  showHotel?: boolean
  showHiddenFromGenericProgram?: boolean
}

export default function ResourceGridCalendar({
  data,
  disableEventClick,
  showWithoutSession = true,
  showHotel = false,
  showHiddenFromGenericProgram = false,
}: ResourceGridCalendarProps) {
  const { events, sessions, slotMinTime, slotMaxTime, resources } = useCalendarData(data, {
    showWithoutSession,
    showHotel,
    showHiddenFromGenericProgram,
  })
  const navigate = useNavigate()
  const theme = useTheme()
  const { i18n, t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [slotMinTimeSingleDay, setMinTime] = useState(slotMinTime)
  const [slotMaxTimeSingleDay, setMaxTime] = useState(slotMaxTime)

  return (
    <FullCalendar
      // TODO buy https://fullcalendar.io/docs/premium
      schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
      plugins={[resourceTimelinePlugin]}
      initialView="resourceTimelineDay"
      validRange={{
        start: data?.event.start,
        end: data?.event.end,
      }}
      timeZone="local"
      locale={i18n.resolvedLanguage}
      height="auto"
      resources={resources}
      views={{
        resourceTimelineDay: {
          buttonText: 'Jour',
          type: 'resourceTimelineDay',
          duration: { days: 1 },
          slotDuration: '00:30:00',
          visibleRange: {
            start: data?.event?.start,
            end: data?.event?.end,
          },
        },
        resourceTimeline4Days: {
          type: 'resourceTimeline',
          buttonText: 'Semaine',
          visibleRange: {
            start: data?.event?.start,
            end: data?.event?.end,
          },
        },
      }}
      resourceAreaHeaderContent={t('Programmes')}
      resourceAreaWidth={isMobile ? '75px' : '200px'}
      datesSet={({ view }) => {
        onDateSet(
          view,
          ['resourceTimelineDay'],
          data?.subEvents,
          [slotMinTimeSingleDay, setMinTime],
          [slotMaxTimeSingleDay, setMaxTime],
          slotMinTime,
          slotMaxTime
        )
      }}
      slotMinTime={slotMinTime}
      slotMaxTime={slotMaxTime}
      slotDuration="00:15:00"
      headerToolbar={{
        left: 'resourceTimeline4Days,resourceTimelineDay',
        center: 'prev,title,next',
        right: '',
      }}
      events={sessions}
      eventClick={info => {
        if (disableEventClick) return
        const extendedProps = (info.event.extendedProps as ExtendedProps) ?? {}
        // const subEventId = extendedProps?.subEventId.toString() ?? ''
        const sessionId = extendedProps?.sessionId.toString() ?? ''
        navigate(`session/${sessionId}?subEventIds=${extendedProps?.subEventId.toString()}`)
      }}
      eventDidMount={info => {
        info.el.style.backgroundColor = `color-mix(in srgb, #676c79 5%, white)`
        info.el.style.borderColor = `color-mix(in srgb, ${info.event.borderColor} 25%, white)`
        info.el.style.borderWidth = '2px'
        info.el.style.fontWeight = '600'

        const descEl = info.el.getElementsByClassName('fc-event-title')[0] as HTMLElement
        const timeEl = info.el.getElementsByClassName('fc-event-time')[0] as HTMLElement

        if (timeEl) timeEl.style.color = `${info.event.borderColor}`

        if (info.event.borderColor === '#676c79') {
          info.el.style.backgroundColor = `color-mix(in srgb, ${info.event.borderColor} 15%, white)`
          info.el.style.borderColor = `color-mix(in srgb, ${info.event.borderColor} 25%, white)`
          if (descEl) descEl.style.color = `${info.event.borderColor}`
          if (timeEl) timeEl.style.color = `${info.event.borderColor}`
        }

        if (info.event.extendedProps.type === 'thoracique') {
          // info.el.style.backgroundColor = `color-mix(in srgb, #c1e4f8 25%, white)`;
          // info.el.style.borderColor = `#c1e4f8`;
        } else if (info.event.extendedProps.type === 'cardiaque') {
          // info.el.style.backgroundColor = `color-mix(in srgb, #e8cecc 25%, white)`;
          // info.el.style.borderColor = `#e8cecc`;
        }

        if (descEl) descEl.style.verticalAlign = 'middle'

        const mainEl = info.el.getElementsByClassName('fc-event-main')[0] as HTMLElement
        if (mainEl) {
          mainEl.style.overflow = 'auto'
        }
      }}
      eventContent={info => {
        const extendedProps = (info.event.extendedProps as ExtendedProps) ?? {}

        const salles = `<div style="display:inline-flex;gap:5px;align-items:center;flex-wrap: wrap;">${extendedProps.salles?.map(salle => `<a style="display:inline-flex;align-items:center;">${salle.groupId === extendedProps.location ? roomSVG(info.event.borderColor) : outsideRoomSVG(info.event.borderColor)}${salle.name}</a>`).join('')}</div>`

        const sessions = extendedProps?.sessions.reduce((acc, el) => {
          if (el.shortName) {
            acc.push(
              `<div style="display:inline-flex;gap:5px;align-items:center;background-color:${el.color};color:${theme.palette.getContrastText(el.color ?? 'grey')};border-radius:0.5rem;padding:0.2rem 0.5rem;font-weight:bold;"><a style="flex:1 0 auto;">${el.shortName}</a></div>`
            )
          }
          return acc
        }, [] as string[])

        const icon = document.createElement('img')
        if (extendedProps.type?.includes('Thoracique')) {
          icon.src = lungsSrc
          icon.style.height = '20px'
          icon.style.width = '20px'
        }
        if (extendedProps.type?.includes('Congénital')) {
          icon.src = congenitalSrc
          icon.style.height = '20px'
          icon.style.width = '20px'
        }
        if (extendedProps.type?.includes('Vasculaire')) {
          icon.src = vasculaireSrc
          icon.style.height = '20px'
          icon.style.width = '20px'
        }
        if (extendedProps.type?.includes('Cardiaque')) {
          icon.src = heartSrc
          icon.style.height = '20px'
          icon.style.width = '20px'
        }

        const name = `${formatHumanTime(info.event.start?.toISOString())} - ${formatHumanTime(info.event.end?.toISOString())} : ${info.event.title}`
        const title = `<div style="display:flex;flex-direction:column;gap:5px;align-items:start;justify-content:space-between;color:${info.event.borderColor};flex-wrap: wrap;"><div style="display:flex;flex-direction:row;gap:5px;align-items:center;justify-content:space-between;width:100%;"><strong>${name}</strong> ${icon.outerHTML}</div><div style="align-items: center;display: flex; gap: 1rem;">${sessions.join('')}${salles}</div>${extendedProps?.description ? `<div style="font-weight:500;">${extendedProps.description.slice(0, 100)}</div>` : ''}</div>`
        const from = extendedProps?.etablissement
          ? `<div style="display:inline-flex;gap:5px;align-items:center;flex-wrap: wrap;">${placeSVG(info.event.borderColor)}${extendedProps.etablissement}</div>`
          : ''
        const contributors = extendedProps?.contributors
          ? extendedProps.contributors
              .slice(0, 2)
              .map(
                el =>
                  `<div style="display:inline-flex;gap:5px;align-items:center;">${selectContributorSVG(el.roles, info.event.borderColor)}<a style="color:${info.event.borderColor || 'black'};text-decoration:none;flex:1 0 auto;">${el.user.first_name} ${el.user.last_name}</a></div>`
              )
              .join('')
              .concat(
                extendedProps.contributors.length > 2
                  ? `<div style="display:inline-flex;gap:5px;align-items:center;">${personSVG(info.event.borderColor)}<a style="color:${info.event.borderColor || 'black'};text-decoration:none;flex:1 0 auto;">+${extendedProps.contributors.length - 2}</a></div>`
                  : ''
              )
          : ''

        return {
          html: `<div>${title}${from || contributors ? `<div style="display:inline-flex;gap:5px;align-items:center;margin-top: 5px;width:100%;flex-wrap: wrap;">${from}${contributors}</div>` : ''}</div>`,
        }
      }}
    />
  )
}
